import React from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import Utils from 'lib/Utils';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { exportCalculatorSettingsRequest, TipCalculator } from 'store/TipCalculator';
import { IStore } from 'store/types';
import { ICalculatorRules, IExportCalculatorSettings, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { AddButton, SettingsSection, TipCalculationsContainer, Title } from '../styles';
import { ImportSettingsModal } from './components/ImportSettingsModal';
import { TipCalculationsDescription } from './components/styles';

interface IImportExportProps {
  calculatorRules?: ICalculatorRules;
}

export const ImportExport: React.FC<IImportExportProps> = ({ calculatorRules }) => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const isExportLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, TipCalculator.EXPORT_CALCULATOR_SETTINGS_REQUEST));
  const exportPermission = hasSitePermission(currentSite?.permissions, PermissionType.CALCULATOR_SETTINGS_EXPORT);
  const importPermission = hasSitePermission(currentSite?.permissions, PermissionType.CALCULATOR_SETTINGS_IMPORT);

  const exportSettings = () => {
    const onSuccess = (response: IExportCalculatorSettings) => Utils.exportToJson(response, `TipCalculatorSettings_${currentSite.name}_${response.exportedAt}`);
    dispatch(exportCalculatorSettingsRequest(currentSite.id, onSuccess));
  };

  const [showImportModal, hideImportModal] = useModal(() => <ImportSettingsModal hideModal={hideImportModal} siteId={currentSite?.id} />, [currentSite]);

  if (!exportPermission && !importPermission) return <></>;

  return (
    <TipCalculationsContainer data-cy="import-export-settings">
      <Title fontWeight="medium">Import/Export Settings</Title>
      <TipCalculationsDescription size="xsmall">
        Quickly replicate calculator rules and segments across multiple sites. Instead of manually configuring rules for each location, simply export your existing setup and import it where needed—saving time and ensuring consistency.
      </TipCalculationsDescription>
      <SettingsSection>
        {exportPermission && <AddButton appearance="white" label="Export Tip Calculator Settings" data-cy="export-button" onClick={exportSettings} isProcessing={isExportLoading} disabled={!calculatorRules} />}
        {importPermission && <AddButton appearance="white" label="Import Tip Calculator Settings" data-cy="import-button" onClick={showImportModal} disabled={isExportLoading} />}
      </SettingsSection>
    </TipCalculationsContainer>
  );
};

export default ImportExport;
